import { useEffect, useState } from 'react';

import {
  invalidateMozartNotifications,
  logAnalyticMozartDefectAlert,
  setNotificationSeenById,
} from 'features/mozart-notifications/infra';
import { useMozartMainDialogLang } from './useMozartMainDialogLang';
import { useStoreSelector } from './useStoreSelector';

import type {
  MozartComplaint,
  MozartComplaints,
  MozartNovelties,
} from 'features/mozart-notifications/domain';
import type { MouseEvent } from 'react';
import type { MenuTab } from '../components';

export function useMozartMainDialog(props: UseMozartMainDialogProps) {
  const { isLoading, novelties, complaints, onSelectComplaint } = props;

  const [menuTabSelected, setMenuTabSelected] = useState<MenuTab>('complaints');

  const lang = useMozartMainDialogLang();

  const storeSelector = useStoreSelector({
    labelOfStoreDefault: lang.filterByStore.labelOfStoreDefault,
  });

  const complaintsToRender = complaints.filter(c =>
    storeSelector.id === 0 ? true : c.storeId === storeSelector.id,
  );

  const showTheTab = {
    novelties: novelties.length > 0,
    complaints: complaints.length > 0,
  };

  const showTheList = {
    novelties: menuTabSelected === 'novelties' && showTheTab.novelties,
    complaints: menuTabSelected === 'complaints' && showTheTab.complaints,
  };

  const numberOfNotSeenToTabs = {
    novelties: novelties.filter(n => !n.seen).length,
    complaints: complaints.filter(c => !c.seen).length,
  };

  const isEmpty = !isLoading && !showTheTab.novelties && !showTheTab.complaints;

  const handleOnSelectNovelties = () => setMenuTabSelected('novelties');

  const handleOnSelectComplaints = () => setMenuTabSelected('complaints');

  const handleClickNoveltiesSection = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const noveltyElement = target.closest('[data-novelty-id]');
    if (!noveltyElement) return;
    const dataset = (noveltyElement as HTMLElement).dataset;
    const [id, seen, createdAt] = [
      parseInt(dataset.noveltyId ?? ''),
      dataset.seen === 'true',
      dataset.createdAt ?? '',
    ];
    if (!id || seen) return;
    setNotificationSeenById({ id, createdAt })
      .then(_ => {
        dataset.seen = 'true';
        invalidateMozartNotifications();
      })
      .catch(err => console.error(err));
  };

  const _sendAnalytics = (complaint: MozartComplaint) => {
    logAnalyticMozartDefectAlert({
      complaint,
      action: 'open_notification',
    });
  };

  const handleClickComplaintsSection = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const btnElement = target.closest('button[data-complaint-id]');
    if (!btnElement) return;
    const id = parseInt((btnElement as HTMLElement).dataset.complaintId ?? '');
    const complaint = complaints.find(c => c.id === id) as MozartComplaint;
    if (!id) return;
    _sendAnalytics(complaint);
    onSelectComplaint(complaint);
  };

  useEffect(() => {
    if (!showTheTab.complaints && showTheTab.novelties) {
      setMenuTabSelected('novelties');
    }
  }, [isLoading, complaints, novelties]);

  return {
    lang,
    isEmpty,
    showTheTab,
    showTheList,
    storeSelector,
    menuTabSelected,
    complaintsToRender,
    numberOfNotSeenToTabs,
    handleOnSelectNovelties,
    handleOnSelectComplaints,
    handleClickNoveltiesSection,
    handleClickComplaintsSection,
  };
}

export interface UseMozartMainDialogProps {
  isLoading: boolean;
  novelties: MozartNovelties;
  complaints: MozartComplaints;
  onSelectComplaint: (c: MozartComplaint) => void;
}
